import { useEffect, useMemo, useState } from 'react';
import { Copy, Layers, Loader2 } from 'lucide-react';
import { copyPackageItems, fetchClonablePackages, type ClonablePackage } from '@/lib/api';
import type { GiftPackage } from '@/lib/types';

interface Props {
  pkg: GiftPackage;
  /** Aangeroepen na een succesvolle kloon zodat de parent kan verversen + doorstappen. */
  onCloned: () => void;
}

const STATUS_TONE: Record<string, string> = {
  definitief: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  alternatief: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
  concept: 'bg-bmade-100 text-bmade-600 dark:bg-bmade-800 dark:text-bmade-300',
};

export function CloneStep({ pkg, onCloned }: Props) {
  const [sources, setSources] = useState<ClonablePackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState<string>('');
  const [variantId, setVariantId] = useState<string>('');
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClonablePackages(pkg.project_id ?? undefined)
      .then((rows) => setSources(rows))
      .catch((e) => {
        // Supabase/PostgREST gooit een plain object (geen Error-instance); pak de echte
        // message zodat een falende query niet stil als generiek "Laden mislukt" verdwijnt.
        console.error('[CloneStep] fetchClonablePackages failed:', e);
        const msg =
          e instanceof Error
            ? e.message
            : e && typeof e === 'object' && 'message' in e
              ? String((e as { message: unknown }).message)
              : 'Laden mislukt';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [pkg.project_id]);

  // Groepeer varianten per project (de "configuratie die je al eens gemaakt hebt").
  const projects = useMemo(() => {
    const map = new Map<string, { id: string; label: string; variants: ClonablePackage[] }>();
    for (const s of sources) {
      const entry = map.get(s.project_id) ?? {
        id: s.project_id,
        label: s.client_name ? `${s.project_name} — ${s.client_name}` : s.project_name,
        variants: [],
      };
      entry.variants.push(s);
      map.set(s.project_id, entry);
    }
    return [...map.values()];
  }, [sources]);

  const variants = useMemo(
    () => projects.find((p) => p.id === projectId)?.variants ?? [],
    [projects, projectId]
  );

  async function handleCopy() {
    if (!variantId) return;
    setCopying(true);
    setError(null);
    try {
      await copyPackageItems(variantId, pkg.id);
      onCloned();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kopiëren mislukt');
    } finally {
      setCopying(false);
    }
  }

  return (
    <div className="flex h-full flex-col gap-5">
      <header>
        <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
          Optionele stap: starten vanaf een eerder pakket
        </div>
        <h2 className="mt-1 text-xl font-semibold text-bmade-950 dark:text-white">
          Kopieer een bestaande configuratie
        </h2>
        <p className="text-sm text-bmade-600 dark:text-bmade-400">
          Kies een eerder samengesteld pakket en de variant die het best past. De items worden naar
          dit pakket gekopieerd, daarna kun je vrij aanpassen. Sla over als je blanco wilt starten.
        </p>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-bmade-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Configuraties laden...
        </div>
      ) : sources.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center text-center text-sm text-bmade-500">
          <Layers className="mb-2 h-6 w-6 text-bmade-400" />
          <p>Nog geen eerdere pakketten met inhoud om van te kopiëren.</p>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Stap 1: kies project/configuratie */}
          <div className="flex flex-col gap-2">
            <label className="text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
              1. Eerder pakket (configuratie)
            </label>
            <select
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                setVariantId('');
              }}
              className="rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white"
            >
              <option value="">Kies een pakket...</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {/* Stap 2: kies variant via radio */}
          <div className="flex flex-col gap-2">
            <label className="text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
              2. Variant om te klonen
            </label>
            {projectId === '' ? (
              <p className="text-xs text-bmade-400">Kies eerst een pakket.</p>
            ) : (
              <div className="space-y-1.5">
                {variants.map((v) => (
                  <label
                    key={v.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                      variantId === v.id
                        ? 'border-accent-400 bg-accent-50 dark:border-accent-500/50 dark:bg-accent-500/10'
                        : 'border-bmade-200 hover:border-bmade-400 dark:border-bmade-700 dark:hover:border-bmade-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="clone-variant"
                      value={v.id}
                      checked={variantId === v.id}
                      onChange={() => setVariantId(v.id)}
                      className="accent-accent-500"
                    />
                    <span className="font-medium text-bmade-950 dark:text-white">
                      {v.variant_label ?? 'Standaard'}
                    </span>
                    {v.variant_status && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          STATUS_TONE[v.variant_status] ?? STATUS_TONE.concept
                        }`}
                      >
                        {v.variant_status}
                      </span>
                    )}
                    <span className="ml-auto text-[11px] text-bmade-500">
                      {v.item_count} items · {v.quantity} pakketten
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {sources.length > 0 && (
        <button
          type="button"
          onClick={handleCopy}
          disabled={!variantId || copying}
          className="inline-flex items-center justify-center gap-2 self-start rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
        >
          {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
          {copying ? 'Kopiëren...' : 'Kopieer items naar dit pakket'}
        </button>
      )}
    </div>
  );
}
